class ProductBundlePicker extends HTMLElement {
  connectedCallback() {
    this.productInfo = this.closest('product-info');
    this.anchorProductId = this.dataset.anchorProductId || '';

    this.discount = this.readEmbeddedJson('[data-bundle-discount]');
    this.freeConfig = this.readEmbeddedJson('[data-bundle-free-config]');

    this.onSelectionChange = this.onSelectionChange.bind(this);
    this.onOptionClick = this.onOptionClick.bind(this);
    this.onSubmitHandler = this.onSubmitHandler.bind(this);

    this.addEventListener('change', this.onSelectionChange);
    this.addEventListener('click', this.onOptionClick);

    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.onSubmitHandler);
    }

    this.renderDiscountOffer();
    this.onSelectionChange();
    this.initTooltips();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onSelectionChange);
    this.removeEventListener('click', this.onOptionClick);
    if (this.submitButton && this.onSubmitHandler) {
      this.submitButton.removeEventListener('click', this.onSubmitHandler);
    }

    if (!this.tooltipWraps || !this.tooltipWraps.length) return;

    this.tooltipWraps.forEach((wrap) => {
      const trigger = wrap.querySelector('[data-bundle-tooltip-trigger]');
      if (trigger && this.onTooltipTriggerClick) {
        trigger.removeEventListener('click', this.onTooltipTriggerClick);
      }
    });

    if (this.onDocumentPointerDown) {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }
    if (this.onTooltipKeydown) {
      document.removeEventListener('keydown', this.onTooltipKeydown);
    }
  }

  // Matches snippets/back-in-stock.liquid: show when unavailable and not preorder.
  isPreorderQuantityAndPolicy(quantity, policy) {
    return (Number.isFinite(quantity) ? quantity : 0) <= 0 && policy === 'continue';
  }

  isPreorderInput(input) {
    if (!input) return false;
    const quantity = parseInt(input.dataset.inventoryQuantity, 10);
    return this.isPreorderQuantityAndPolicy(quantity, input.dataset.inventoryPolicy);
  }

  isPreorderFreeVariant() {
    if (!this.freeConfig?.freeVariant) return false;
    const freeVariant = this.freeConfig.freeVariant;
    const quantity = parseInt(freeVariant.inventoryQuantity, 10);
    return this.isPreorderQuantityAndPolicy(quantity, freeVariant.inventoryPolicy);
  }

  isNotifyMeVariant(input) {
    if (!input) return false;

    const available = input.dataset.available === 'true';
    return !available && !this.isPreorderInput(input);
  }

  selectionHasPreorder(selected, state) {
    if ((selected || []).some((input) => this.isPreorderInput(input))) return true;
    if (state && state.isFreeProduct && state.qualified && this.isPreorderFreeVariant()) return true;
    return false;
  }

  get shipStatusElement() {
    return this._shipStatusElement =
      this._shipStatusElement || this.querySelector('[data-bundle-ship-status]');
  }

  trackLastSelectedInput(event) {
    const input = event?.target;
    if (!input?.matches?.('input[data-variant-id]')) return;
    if (this.isNotifyMeVariant(input)) return;

    this.selectionOrder = this.selectionOrder || [];

    if (input.checked) {
      this.selectionOrder = this.selectionOrder.filter((el) => el !== input && el.isConnected && el.checked);
      this.selectionOrder.push(input);
    } else {
      this.selectionOrder = this.selectionOrder.filter((el) => el !== input && el.isConnected && el.checked);
    }

    this.lastSelectedInput = this.selectionOrder[this.selectionOrder.length - 1] || null;
  }

  get mediaGallery() {
    if (this._mediaGallery?.isConnected) return this._mediaGallery;
    const product = this.closest('.product') || this.closest('product-info') || document;
    this._mediaGallery = product.querySelector('media-gallery');
    return this._mediaGallery;
  }

  getPreviewImageInput() {
    const order = this.selectionOrder || [];
    for (let i = order.length - 1; i >= 0; i -= 1) {
      const input = order[i];
      if (input?.isConnected && input.checked && input.dataset.imageUrl) return input;
    }
    return null;
  }

  buildImageSrcset(url, maxWidth, widths) {
    if (!url) return '';
    const limit = parseInt(maxWidth, 10) || 2000;
    return widths
      .filter((width) => width <= limit)
      .map((width) => {
        const src = /([?&])width=\d+/.test(url)
          ? url.replace(/([?&])width=\d+/, `$1width=${width}`)
          : `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
        return `${src} ${width}w`;
      })
      .join(', ');
  }

  applyImageToElement(img, { url, width, height, alt, sizes, widths }) {
    if (!img || !url) return;
    img.src = url;
    img.srcset = this.buildImageSrcset(url, width, widths);
    img.sizes = sizes;
    img.alt = alt || '';
    if (width) img.setAttribute('width', width);
    if (height) img.setAttribute('height', height);
    img.setAttribute('is', 'lazy-image');
    img.classList.add('w-full');

    const markLoaded = () => {
      img.classList.add('loaded');
      img.closest('.media')?.classList.remove('loading');
    };
    if (img.complete && img.naturalWidth > 0) {
      markLoaded();
    } else {
      img.addEventListener('load', markLoaded, { once: true });
    }
  }

  createBundlePreviewSlide(gallery, imageData) {
    const list = gallery.sliderGallery?.querySelector('.product__media-list');
    if (!list) return null;

    const sibling = list.querySelector('.product__media:not([data-media-id="bundle-preview"])');
    const slide = document.createElement('div');
    slide.className = sibling
      ? sibling.className
          .split(/\s+/)
          .filter((cls) => this.keepMediaClass(cls, true))
          .join(' ')
      : 'product__media card media flex w-full flex-auto relative overflow-hidden';
    slide.classList.add('product__media--first');
    slide.dataset.mediaType = 'image';
    slide.dataset.mediaId = 'bundle-preview';
    slide.setAttribute('data-gang-default', '');

    const img = document.createElement('img');
    img.loading = 'eager';
    img.setAttribute('fetchpriority', 'high');
    this.applyImageToElement(img, {
      ...imageData,
      sizes: sibling?.querySelector('img')?.sizes || '(max-width: 740px) calc(100vw - 40px), (max-width: 999px) calc(100vw - 64px), 800px',
      widths: [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800, 3000, 3200]
    });
    slide.appendChild(img);

    const zoomButton = sibling?.querySelector('button[is^="media-"]');
    if (zoomButton) {
      slide.appendChild(zoomButton.cloneNode(true));
    }

    return slide;
  }

  createBundlePreviewThumbnail(gallery, imageData) {
    const dots = gallery.sliderDots;
    if (!dots) return null;

    const sibling = dots.querySelector('.product__thumbnail:not([data-media-id="bundle-preview"])');
    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = sibling
      ? sibling.className
          .split(/\s+/)
          .filter((cls) => this.keepMediaClass(cls, false))
          .join(' ')
      : 'product__thumbnail media media--square relative overflow-hidden';
    thumb.dataset.mediaId = 'bundle-preview';
    thumb.setAttribute('data-gang-default', '');
    thumb.setAttribute('aria-current', 'true');
    thumb.setAttribute('aria-label', 'Bundle selection preview');

    const img = document.createElement('img');
    img.loading = 'lazy';
    this.applyImageToElement(img, {
      ...imageData,
      sizes: sibling?.querySelector('img')?.sizes || '(max-width: 1280px) 300px, 300px',
      widths: [54, 74, 104, 162, 208, 324, 416]
    });
    thumb.appendChild(img);

    thumb.addEventListener('click', (event) => {
      event.preventDefault();
      const index = parseInt(thumb.getAttribute('data-index'), 10) || 1;
      gallery.sliderGallery?.select(index);
      dots.querySelectorAll('.product__thumbnail').forEach((el) => {
        el.setAttribute('aria-current', el === thumb ? 'true' : 'false');
      });
    });

    return thumb;
  }

  keepMediaClass(cls, isSlide) {
    if (!cls) return false;
    if (cls === 'hidden' || cls === 'loading') return false;
    if (/^(sm|md|lg|xl|xxl):hidden$/.test(cls)) return false;
    if (isSlide && (cls === 'product__media--first' || cls === 'product__media--variant')) return false;
    return true;
  }

  syncStickyPreview(gallery, slide) {
    if (!gallery?.mediaPreview || !slide) return;

    gallery.sliderGallery
      ?.querySelectorAll('[data-media-id]')
      .forEach((media) => media.classList.remove('xl:hidden'));

    const clone = slide.cloneNode(true);
    clone.classList.remove('xl:hidden', 'xxl:hidden');
    gallery.mediaPreview.parentNode.replaceChild(clone, gallery.mediaPreview);
    slide.classList.add('xl:hidden');
  }

  restoreStickyPreview(gallery) {
    if (!gallery?.mediaPreview) return;

    const sticky = gallery.mediaPreview;
    if (sticky.dataset.mediaId !== 'bundle-preview') return;

    const firstReal = gallery.sliderGallery?.querySelector(
      '.product__media:not([data-media-id="bundle-preview"]):not([hidden])'
    );
    if (!firstReal) {
      sticky.remove();
      return;
    }

    gallery.sliderGallery
      ?.querySelectorAll('[data-media-id]')
      .forEach((media) => media.classList.remove('xl:hidden'));

    const clone = firstReal.cloneNode(true);
    clone.classList.remove('xl:hidden', 'xxl:hidden');
    sticky.parentNode.replaceChild(clone, sticky);
    firstReal.classList.add('xl:hidden');
  }

  resetGallerySliders(gallery, selectFirst = false) {
    if (!gallery?.sliderGallery) return;

    gallery.sliderGallery.reset();
    if (gallery.sliderDots) {
      gallery.sliderDots.reset();
      gallery.sliderDots.resetIndexes();
    }
    if (selectFirst) {
      gallery.sliderGallery.select(1, true);
      gallery.sliderDots?.transitionTo?.(0, true);
    }
    gallery.countMediaGallery?.();
  }

  removeBundleGalleryPreview(gallery) {
    if (!gallery?.sliderGallery) return;

    const slides = gallery.sliderGallery.querySelectorAll('[data-media-id="bundle-preview"]');
    const thumbs = gallery.sliderDots?.querySelectorAll('[data-media-id="bundle-preview"]') || [];
    const stickyIsPreview = gallery.mediaPreview?.dataset.mediaId === 'bundle-preview';
    if (!slides.length && !thumbs.length && !stickyIsPreview) return;

    slides.forEach((el) => el.remove());
    thumbs.forEach((el) => el.remove());

    this.restoreStickyPreview(gallery);
    this.resetGallerySliders(gallery, true);
  }

  updateGalleryPreview() {
    const gallery = this.mediaGallery;
    if (!gallery?.sliderGallery) return;

    const input = this.getPreviewImageInput();
    if (!input) {
      this.removeBundleGalleryPreview(gallery);
      return;
    }

    const imageData = {
      url: input.dataset.imageUrl,
      width: input.dataset.imageWidth,
      height: input.dataset.imageHeight,
      alt: input.dataset.imageAlt || ''
    };

    const list = gallery.sliderGallery.querySelector('.product__media-list');
    if (!list) return;

    let slide = list.querySelector('[data-media-id="bundle-preview"]');
    if (slide) {
      this.applyImageToElement(slide.querySelector('img'), {
        ...imageData,
        sizes: slide.querySelector('img')?.sizes || '(max-width: 740px) calc(100vw - 40px), (max-width: 999px) calc(100vw - 64px), 800px',
        widths: [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800, 3000, 3200]
      });
    } else {
      slide = this.createBundlePreviewSlide(gallery, imageData);
      if (!slide) return;
    }

    list.prepend(slide);

    const dots = gallery.sliderDots;
    if (dots) {
      let thumb = dots.querySelector('[data-media-id="bundle-preview"]');
      if (thumb) {
        this.applyImageToElement(thumb.querySelector('img'), {
          ...imageData,
          sizes: thumb.querySelector('img')?.sizes || '(max-width: 1280px) 300px, 300px',
          widths: [54, 74, 104, 162, 208, 324, 416]
        });
      } else {
        thumb = this.createBundlePreviewThumbnail(gallery, imageData);
      }

      if (thumb) {
        dots.prepend(thumb);
        dots.querySelectorAll('.product__thumbnail').forEach((el) => {
          el.setAttribute('aria-current', el.dataset.mediaId === 'bundle-preview' ? 'true' : 'false');
        });
      }
    }

    this.syncStickyPreview(gallery, slide);
    this.resetGallerySliders(gallery, true);
  }

  updateShipStatus() {
    const el = this.shipStatusElement;
    if (!el) return;

    const input = this.lastSelectedInput;
    if (!input || !input.checked) {
      el.textContent = '';
      el.setAttribute('hidden', '');
      return;
    }

    if (this.isPreorderInput(input)) {
      const date = (input.dataset.preorderDate || '').trim();
      if (!date) {
        el.textContent = '';
        el.setAttribute('hidden', '');
        return;
      }
      el.textContent = `Estimated Ship Date ${date}`;
      el.removeAttribute('hidden');
      return;
    }

    el.textContent = el.dataset.inStockText || 'In stock, ready to ship';
    el.removeAttribute('hidden');
  }

  updateSubmitLabel(selected, state) {
    if (!this.submitButton) return;

    const label = this.submitButton.querySelector('.btn-text') || this.submitButton;
    const isPreorder = this.selectionHasPreorder(selected, state);
    label.textContent = isPreorder
      ? (theme.variantStrings?.preOrder || 'Pre-Order')
      : (theme.variantStrings?.addToCart || 'Add to cart');
  }

  setBackInStockVisible(show, variantId) {
    const backInStockSection = document.getElementById('back-in-stock-section');
    if (!backInStockSection) return;

    backInStockSection.classList.toggle('hidden', !show);

    if (!show || !variantId) return;

    const selectedVariantInput = document.getElementById('selected-variant-id');
    if (selectedVariantInput) selectedVariantInput.value = String(variantId);

    const form = document.querySelector('[data-back-in-stock-form]');
    if (form) form.setAttribute('data-variant-id', String(variantId));
  }

  onOptionClick(event) {
    const label = event.target.closest('label.variant-selector');
    if (!label || !this.contains(label)) return;

    const inputId = label.getAttribute('for');
    const input = inputId ? this.querySelector(`#${CSS.escape(inputId)}`) : null;
    if (!input || !input.matches('input[data-variant-id]')) return;

    if (this.isNotifyMeVariant(input)) {
      event.preventDefault();
      this.setBackInStockVisible(true, input.dataset.variantId);
      return;
    }

    this.setBackInStockVisible(false);
  }

  initTooltips() {
    this.tooltipWraps = Array.from(this.querySelectorAll('.bundle-tooltip-wrap'));
    if (!this.tooltipWraps.length) return;

    this.onTooltipTriggerClick = this.onTooltipTriggerClick.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onTooltipKeydown = this.onTooltipKeydown.bind(this);

    this.tooltipWraps.forEach((wrap) => {
      const trigger = wrap.querySelector('[data-bundle-tooltip-trigger]');
      if (trigger) trigger.addEventListener('click', this.onTooltipTriggerClick);
    });

    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onTooltipKeydown);
  }

  onTooltipTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const wrap = event.currentTarget.closest('.bundle-tooltip-wrap');
    if (!wrap) return;

    const willOpen = !wrap.classList.contains('is-open');
    this.closeAllTooltips();
    if (willOpen) this.openTooltip(wrap);
  }

  openTooltip(wrap) {
    const trigger = wrap.querySelector('[data-bundle-tooltip-trigger]');
    const tip = wrap.querySelector('[data-bundle-tooltip]');
    wrap.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (tip) tip.removeAttribute('hidden');
  }

  closeAllTooltips() {
    if (!this.tooltipWraps) return;

    this.tooltipWraps.forEach((wrap) => {
      const trigger = wrap.querySelector('[data-bundle-tooltip-trigger]');
      const tip = wrap.querySelector('[data-bundle-tooltip]');
      wrap.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (tip) tip.setAttribute('hidden', '');
    });
  }

  onDocumentPointerDown(event) {
    if (event.target.closest('.bundle-tooltip-wrap')) return;
    this.closeAllTooltips();
  }

  onTooltipKeydown(event) {
    if (event.key === 'Escape') this.closeAllTooltips();
  }

  get submitButton() {
    return this._submitButton =
      this._submitButton ||
      (this.productInfo && this.productInfo.querySelector('[data-bundle-submit]'));
  }

  get drawerButton() {
    return document.querySelector('[data-pdp-cart-checkout]');
  }

  getActionButtons() {
    return [this.submitButton, this.drawerButton].filter(Boolean);
  }

  get totalElements() {
    return this._totalElements =
      this._totalElements ||
      (this.productInfo
        ? Array.from(this.productInfo.querySelectorAll('[data-bundle-total]'))
        : []);
  }

  get errorMessage() {
    return this._errorMessage =
      this._errorMessage ||
      (this.productInfo && this.productInfo.querySelector('.product-form__error-message'));
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get discountUi() {
    return this._discountUi = this._discountUi || this.querySelector('[data-bundle-discount-ui]');
  }

  get offerElement() {
    return this._offerElement = this._offerElement || this.querySelector('[data-bundle-discount-offer]');
  }

  get progressElement() {
    return this._progressElement = this._progressElement || this.querySelector('[data-bundle-discount-progress]');
  }

  get savingsElement() {
    return this._savingsElement = this._savingsElement || this.querySelector('[data-bundle-discount-savings]');
  }

  // The discount type is only actionable when its config is complete; for
  // free_product that means the free variant was resolved server-side.
  get discountType() {
    if (!this.discount || !this.discount.type || this.discount.type === 'none') return null;
    if (this.discount.type === 'free_product') {
      if (!this.freeConfig || !this.freeConfig.freeVariant || !this.freeConfig.freeVariant.id) return null;
    }
    return this.discount.type;
  }

  readEmbeddedJson(selector) {
    const script = this.querySelector(selector);
    if (!script) return null;

    try {
      const parsed = JSON.parse(script.textContent);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  // Metafield money values are decimal strings in the shop currency (e.g. "200").
  toCents(value) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
  }

  formatMoney(cents) {
    return theme.utils.formatMoney(cents, theme.settings.moneyFormat);
  }

  getSelectedInputs() {
    return Array.from(this.querySelectorAll('input[type="checkbox"][data-variant-id]:checked:not(:disabled)'));
  }

  getSelectedSubtotal(selected) {
    return selected.reduce((sum, input) => sum + (parseInt(input.dataset.price, 10) || 0), 0);
  }

  rewardText() {
    if (this.discount.value_type === 'percentage') {
      return `${parseFloat(this.discount.value) || 0}%`;
    }
    return this.formatMoney(this.toCents(this.discount.value));
  }

  rewardAmount(subtotal) {
    if (this.discount.value_type === 'percentage') {
      const percentage = parseFloat(this.discount.value) || 0;
      return Math.round(subtotal * percentage / 100);
    }
    return Math.min(this.toCents(this.discount.value), subtotal);
  }

  renderDiscountOffer() {
    if (!this.discountUi) return;

    const type = this.discountType;
    if (!type) {
      this.discountUi.setAttribute('hidden', '');
      return;
    }

    let offer = '';
    if (type === 'spend_amount') {
      const minimum = this.formatMoney(this.toCents(this.discount.minimum_amount));
      offer = `Spend ${minimum} across this bundle, get ${this.rewardText()} off`;
    } else if (type === 'quantity') {
      const minimum = parseInt(this.discount.minimum_quantity, 10) || 0;
      offer = `Buy ${minimum}+ items in this bundle, get ${this.rewardText()} off`;
    } else if (type === 'free_product') {
      const buyTitles = (this.freeConfig.buyVariants || []).map((variant) => variant.title).filter(Boolean);
      const freeTitle = this.freeConfig.freeVariant.title || 'a free item';
      offer = buyTitles.length
        ? `Buy ${buyTitles.join(', ')}, get ${freeTitle} free`
        : `Buy the required items, get ${freeTitle} free`;
    }

    if (this.offerElement) this.offerElement.textContent = offer;
    this.discountUi.removeAttribute('hidden');
  }

  // Thresholds count only the variants selected in the picker.
  getDiscountState(selected) {
    const type = this.discountType;
    if (!type) return null;

    const subtotal = this.getSelectedSubtotal(selected);
    const lineCount = selected.length;

    if (type === 'spend_amount') {
      const minimum = this.toCents(this.discount.minimum_amount);
      const qualified = subtotal >= minimum;
      return {
        qualified,
        savings: qualified ? this.rewardAmount(subtotal) : 0,
        isFreeProduct: false,
        progressText: qualified ? '' : `${this.formatMoney(minimum - subtotal)} more to unlock`
      };
    }

    if (type === 'quantity') {
      const minimum = parseInt(this.discount.minimum_quantity, 10) || 0;
      const qualified = lineCount >= minimum;
      const remaining = minimum - lineCount;
      return {
        qualified,
        savings: qualified ? this.rewardAmount(subtotal) : 0,
        isFreeProduct: false,
        progressText: qualified ? '' : `${remaining} more item${remaining === 1 ? '' : 's'} to unlock`
      };
    }

    const owned = new Set(selected.map((input) => String(input.dataset.variantId)));
    const missing = (this.freeConfig.buyVariants || []).filter((variant) => !owned.has(String(variant.id)));
    const qualified = missing.length === 0;
    return {
      qualified,
      savings: qualified ? (parseInt(this.freeConfig.freeVariant.price, 10) || 0) : 0,
      isFreeProduct: true,
      progressText: qualified ? '' : `${missing.length} more required item${missing.length === 1 ? '' : 's'} to unlock`
    };
  }

  onSelectionChange(event) {
    if (
      event?.target?.matches?.('input[data-variant-id]') &&
      !this.isNotifyMeVariant(event.target)
    ) {
      this.setBackInStockVisible(false);
    }

    this.trackLastSelectedInput(event);

    const selected = this.getSelectedInputs();
    const subtotal = this.getSelectedSubtotal(selected);
    const state = this.getDiscountState(selected);

    this.totalElements.forEach((el) => {
      if (selected.length === 0) {
        el.innerHTML = '';
        el.setAttribute('hidden', '');
        return;
      }

      // Display-only estimate; Shopify's Discount Function is the source of truth.
      if (state && state.qualified && state.savings > 0) {
        const compareAt = state.isFreeProduct ? subtotal + state.savings : subtotal;
        const estimated = state.isFreeProduct ? subtotal : Math.max(subtotal - state.savings, 0);
        el.innerHTML = `<s class="bundle-total__compare">${this.formatMoney(compareAt)}</s><span class="bundle-total__price">${this.formatMoney(estimated)}</span>`;
      } else {
        el.innerHTML = this.formatMoney(subtotal);
      }
      el.removeAttribute('hidden');
    });

    if (state && this.discountUi) {
      if (state.qualified) {
        if (this.progressElement) this.progressElement.setAttribute('hidden', '');
        if (this.savingsElement) {
          this.savingsElement.textContent = state.isFreeProduct
            ? `Free item unlocked · ${this.freeConfig.freeVariant.title || 'Free item'}`
            : `Discount unlocked · Save ${this.formatMoney(state.savings)} (est.)`;
          this.savingsElement.removeAttribute('hidden');
        }
      } else {
        if (this.savingsElement) this.savingsElement.setAttribute('hidden', '');
        if (this.progressElement) {
          this.progressElement.textContent = state.progressText;
          this.progressElement.removeAttribute('hidden');
        }
      }
    }

    if (this.submitButton) {
      if (selected.length > 0) {
        this.submitButton.removeAttribute('disabled');
      } else {
        this.submitButton.setAttribute('disabled', '');
      }
    }

    this.updateShipStatus();
    this.updateSubmitLabel(selected, state);
    this.updateGalleryPreview();
    this.publishDrawerState();
  }

  buildDrawerItems(selected, state) {
    const items = selected.map((input) => {
      const price = parseInt(input.dataset.price, 10) || 0;
      return {
        variantId: String(input.dataset.variantId || ''),
        title: input.dataset.title || input.dataset.imageAlt || '',
        imageUrl: input.dataset.imageUrl || '',
        price,
        compareAt: price,
        preorderDate: this.isPreorderInput(input) ? (input.dataset.preorderDate || '').trim() : '',
        isFree: false
      };
    });

    if (state && state.isFreeProduct && state.qualified && this.freeConfig?.freeVariant) {
      const freeVariant = this.freeConfig.freeVariant;
      const original = parseInt(freeVariant.price, 10) || 0;
      items.push({
        variantId: String(freeVariant.id),
        title: freeVariant.title || 'Free item',
        imageUrl: freeVariant.imageUrl || '',
        price: 0,
        compareAt: original,
        preorderDate: this.isPreorderFreeVariant() ? (freeVariant.preorderDate || '').trim() : '',
        isFree: true
      });
    }

    return items;
  }

  getDrawerPayload() {
    const selected = this.getSelectedInputs();
    const subtotal = this.getSelectedSubtotal(selected);
    const state = this.getDiscountState(selected);
    const items = this.buildDrawerItems(selected, state);
    const hasDiscount = Boolean(state && state.qualified && state.savings > 0);
    const compareAt = hasDiscount
      ? (state.isFreeProduct ? subtotal + state.savings : subtotal)
      : subtotal;
    const total = hasDiscount
      ? (state.isFreeProduct ? subtotal : Math.max(subtotal - state.savings, 0))
      : subtotal;

    return { items, subtotal, compareAt, total, hasDiscount };
  }

  publishDrawerState() {
    const eventName = theme.pubsub?.PUB_SUB_EVENTS?.bundleSelectionChange;
    if (!eventName) return;

    theme.pubsub.publish(eventName, {
      source: 'product-bundle-picker',
      ...this.getDrawerPayload()
    });
  }

  uncheckVariant(variantId) {
    const id = String(variantId || '');
    if (!id) return;

    const input = this.querySelector(`input[data-variant-id="${id}"]`);
    if (!input || !input.checked) return;

    input.checked = false;
    this.onSelectionChange({ target: input });
  }

  clearSelection() {
    this.getSelectedInputs().forEach((input) => {
      input.checked = false;
    });
    this.selectionOrder = [];
    this.lastSelectedInput = null;
    this.onSelectionChange();
  }

  clickedActionButton(event) {
    return event?.target?.closest?.('[data-pdp-cart-checkout], [data-bundle-submit]')
      || event?.currentTarget
      || this.submitButton;
  }

  setActionButtonsBusy(busy) {
    this.getActionButtons().forEach((button) => {
      if (busy) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      } else {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
      }
    });
  }

  restoreActionButtons() {
    const selected = this.getSelectedInputs();
    const hasItems = selected.length > 0;

    if (this.submitButton) {
      if (hasItems) this.submitButton.removeAttribute('disabled');
      else this.submitButton.setAttribute('disabled', '');
    }

    this.publishDrawerState();
  }

  // Every line (selections, free item) carries _pp_bundle so the app's Discount
  // Function can group them and resolve the anchor product's config by that id.
  buildLineProperties(preorderDate) {
    const properties = { _pp_bundle: String(this.anchorProductId) };
    const date = (preorderDate || '').trim();
    if (date) properties['Estimated Ship Date'] = date;
    return properties;
  }

  buildItems(selected, state) {
    const items = selected.map((input) => ({
      id: input.dataset.variantId,
      quantity: 1,
      properties: this.buildLineProperties(
        this.isPreorderInput(input) ? input.dataset.preorderDate : ''
      )
    }));

    if (state && state.isFreeProduct && state.qualified) {
      const freeVariant = this.freeConfig.freeVariant;
      items.push({
        id: String(freeVariant.id),
        quantity: 1,
        properties: this.buildLineProperties(
          this.isPreorderFreeVariant() ? freeVariant.preorderDate : ''
        )
      });
    }

    return items;
  }

  // theme.utils.postLink2 only serializes two levels deep, which breaks the
  // nested properties object, so build the cart form fields here instead.
  postBundleForm(items) {
    const form = document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('action', theme.routes.cart_add_url);

    const addField = (name, value) => {
      const field = document.createElement('input');
      field.setAttribute('type', 'hidden');
      field.setAttribute('name', name);
      field.setAttribute('value', value);
      form.appendChild(field);
    };

    items.forEach((item, index) => {
      addField(`items[${index}][id]`, item.id);
      addField(`items[${index}][quantity]`, item.quantity);
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        addField(`items[${index}][properties][${key}]`, value);
      });
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  onSubmitHandler(event) {
    this.submitBundle(event);
  }

  submitBundle(event) {
    const selected = this.getSelectedInputs();
    if (!selected.length) return;

    this.handleErrorMessage();

    const state = this.getDiscountState(selected);
    if (state && state.isFreeProduct && state.qualified && this.freeConfig.freeVariant.available === false) {
      this.handleErrorMessage('The free bundle item is currently unavailable. Please try again later.');
      return;
    }

    const clickedButton = this.clickedActionButton(event);

    if (
      this.selectionHasPreorder(selected, state) &&
      clickedButton &&
      !clickedButton.hasAttribute('data-preorder-confirmed')
    ) {
      if (window.PolarProPreorderModal?.open) {
        event?.preventDefault?.();
        event?.stopImmediatePropagation?.();
        window.PolarProPreorderModal.open({ mode: 'generic', button: clickedButton });
        return;
      }
    }

    const items = this.buildItems(selected, state);

    if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
      this.postBundleForm(items);
      return;
    }

    event?.preventDefault?.();
    if (this.getActionButtons().some((button) => button.hasAttribute('aria-disabled'))) return;
    this.activeElement = clickedButton;

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    const body = JSON.stringify({
      items,
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });

    this.setActionButtonsBusy(true);

    fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          // Transient failure (stock, rate limit, server error): surface the
          // message but leave the button usable so the customer can retry.
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', {
              detail: {
                errorMessage: parsedState.description
              }
            })
          );
          return;
        }

        const cartJson = await (
          await fetch(`${theme.routes.cart_url}`, { ...theme.utils.fetchConfig() })
        ).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-bundle-picker',
          cart: cartJson
        });
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            detail: {
              product: parsedState
            }
          })
        );

        this.cartDrawer?.show(this.activeElement);
        this.clearSelection();
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.setActionButtonsBusy(false);
        this.restoreActionButtons();
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage || '';
  }
}

customElements.define('product-bundle-picker', ProductBundlePicker);
